import { useEffect, useState } from 'react';
import { apiClient } from '../network/ApiClient';
import { useAuth } from '../context/AuthContext';
import { isAppLocale, type AppLocale } from '../i18n/locale';

interface Identity {
  displayName: string;
  /** Selected preset avatar key (Player Identity V1); null = initial fallback. */
  avatarKey:   string | null;
  /** Lowercase two-letter country code; null = neutral globe fallback. */
  countryCode: string | null;
  /** Legacy provider-photo placeholder — backend always sends null today. */
  avatarUrl:   string | null;
  hdrEnabled:  boolean;
  pushEnabled: boolean;
  /** Account-level language preference (Localization Phase 10); null when
   *  the account never chose OR the server sent a value outside the
   *  supported set (see preferredLocaleUnsupported). */
  preferredLocale: AppLocale | null;
  /** True when the server sent a NON-null value outside AppLocale (e.g. a
   *  future locale saved by a newer client). Such a value never enters
   *  i18next and must never be silently overwritten by the null-bootstrap. */
  preferredLocaleUnsupported: boolean;
}

// Module-level cache: the shell chip and Home both need the identity, but the
// backend only needs to be asked once per session.
let cached: Identity | null = null;
let inflight: Promise<Identity | null> | null = null;
// Bumped by clearIdentityCache(); an in-flight fetch that resolves after a clear
// (account switch) checks this and refuses to repopulate the cache for the
// previous identity.
let identityEpoch = 0;

// Player Identity V1 — live subscriber registry. Every currently-mounted
// useIdentity() consumer (Sidebar chip, TopNav button, Home, …) registers
// its setState here. refreshIdentity() pushes the freshly-fetched identity
// to every one of them synchronously, in the SAME route lifecycle — no
// remount, navigation, or second effect-driven fetch required. This is
// deliberately a plain Set of setState callbacks (not a new state library):
// the smallest broadcast primitive that satisfies "every mounted consumer
// updates immediately."
const subscribers = new Set<(identity: Identity | null) => void>();

/**
 * Clear the cached identity. Called centrally on logout / account switch (see
 * AuthContext) so User B never sees User A's cached username/avatar (F2). Also
 * drops any in-flight fetch so its result cannot repopulate the cache for the
 * previous identity.
 *
 * Does NOT notify subscribers — logout/switch also flips `identityKey`/
 * `isLoggedIn`, which already drives every mounted useIdentity() consumer to
 * its own re-fetch (or to null) via the effect below. Use refreshIdentity()
 * instead when the SAME account's identity changed (e.g. a Settings save).
 */
export function clearIdentityCache(): void {
  identityEpoch++;
  cached = null;
  inflight = null;
}

async function fetchIdentity(): Promise<Identity | null> {
  const myEpoch = identityEpoch;
  try {
    const profile = await apiClient.getProfile();
    const rawLocale = profile.preferred_locale ?? null;
    const validLocale = rawLocale !== null && isAppLocale(rawLocale) ? rawLocale : null;
    if (rawLocale !== null && validLocale === null && import.meta.env.DEV) {
      console.warn(`[useIdentity] account preferred_locale ${JSON.stringify(rawLocale)} is not a supported AppLocale — ignoring`);
    }
    const result: Identity = {
      displayName: profile.display_name,
      avatarKey:   profile.avatar_key ?? null,
      countryCode: profile.country_code ?? null,
      avatarUrl:   profile.avatar_url,
      hdrEnabled:  profile.hdr_enabled ?? false,
      pushEnabled: profile.push_enabled ?? false,
      preferredLocale: validLocale,
      preferredLocaleUnsupported: rawLocale !== null && validLocale === null,
    };
    // A logout/switch happened while fetching — do not repopulate the cache for
    // the previous identity.
    if (myEpoch !== identityEpoch) return null;
    cached = result;
    return cached;
  } catch {
    return null; // backend unavailable — callers keep their fallback name
  }
}

/**
 * Player Identity V1 — re-fetch the authoritative identity and broadcast it
 * to every currently-mounted useIdentity() consumer immediately, within the
 * same route lifecycle. Call this after a successful Settings mutation
 * (avatar or country save) instead of clearIdentityCache() — clearing alone
 * only affects the NEXT mount; this updates what's on screen right now.
 *
 * On failure (network error, logout raced the request) the cache and every
 * subscriber are left exactly as they were — a failed save never overwrites
 * the previously-shown authoritative identity with a blank/partial one.
 */
/**
 * Patch the cached identity in place and broadcast to every mounted
 * consumer — WITHOUT a network refetch. Used after a mutation whose
 * authoritative result is already known from its own response (e.g. the
 * account-language save), where a /me refetch would be redundant. No-op
 * while nothing is cached (logged out / pre-first-fetch).
 */
export function patchIdentity(patch: Partial<Identity>): void {
  if (!cached) return;
  cached = { ...cached, ...patch };
  for (const notify of subscribers) notify(cached);
}

export async function refreshIdentity(): Promise<Identity | null> {
  cached = null;
  inflight = null;
  const result = await fetchIdentity();
  if (result) {
    for (const notify of subscribers) notify(result);
  }
  return result;
}

/**
 * Canonical logged-in identity from the backend (username + avatar).
 *
 * Name preference order (the username-first rule): backend display_name →
 * Firebase displayName → email prefix → dev fallback. Returns `null` while
 * loading or logged out — callers apply the fallback chain themselves via
 * `resolveDisplayName`.
 */
export function useIdentity(): Identity | null {
  const { isLoggedIn, identityKey } = useAuth();
  const [identity, setIdentity] = useState<Identity | null>(cached);

  // Register/unregister with the live broadcast set for the component's
  // entire mounted lifetime — independent of identityKey, so a Settings
  // save reaches this consumer whether or not identityKey has changed.
  useEffect(() => {
    subscribers.add(setIdentity);
    return () => { subscribers.delete(setIdentity); };
  }, []);

  // Keyed on identityKey (not the isLoggedIn boolean) so a DIRECT account switch
  // A→B re-fetches B's identity even on already-mounted consumers (the shell
  // chip). AuthContext clears the module cache on switch, so `cached` is null
  // here for B and the fetch runs (F2).
  useEffect(() => {
    if (!isLoggedIn) { setIdentity(null); return; }
    if (cached) { setIdentity(cached); return; }
    inflight ??= fetchIdentity();
    let alive = true;
    inflight.then(result => { if (alive && result) setIdentity(result); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityKey]);

  return identity;
}

/** Shared name-resolution chain: username (backend) → Firebase → email → fallback. */
export function resolveDisplayName(
  identity: Identity | null,
  user: { displayName?: string | null; email?: string | null } | null | undefined,
  fallback: string,
): string {
  return identity?.displayName
    || user?.displayName
    || user?.email?.split('@')[0]
    || fallback;
}
